<script setup>
import BasePage from '@/components/BasePage.vue'
import {useHead} from "@vueuse/head";

const lastUpdated = "July 8, 2025"

useHead({
  title: "Terms of Use – Untracked Apps",
  meta: [
    {
      name: "description",
      content: "Review the Terms of Use for Untracked Apps' apps and services, including privacy, age requirements, and responsibilities."
    },
    {
      name: "robots",
      content: "index,follow"
    }
  ],
  link: [
    {
      rel: "canonical",
      href: "https://untrackedapps.com/terms"
    }
  ]
})
</script>

<template>
  <BasePage>
    <section
        class="min-h-[100vh] px-6 py-10 relative overflow-hidden max-w-3xl mx-auto"
    >
      <header class="text-center">
        <h1 class="text-4xl md:text-5xl font-bold text-gray-700">Terms of Use</h1>
        <p class="mt-4 mb-8 text-lg">Last updated: {{ lastUpdated }}</p>
      </header>
      <section class="space-y-6 text-lg leading-relaxed text-left">
        <section>
          <h2 class="text-xl font-semibold mb-2">1. Who We Are</h2>
          <p>Untracked Apps is a product brand operated by Dominic Juan Lebron and is not a separate legal entity.
            We build software designed to be practical, privacy-conscious, and easy to use.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">2. License and Acceptable Use</h2>
          <p>Subject to these Terms and any applicable app-store terms, we grant you a limited, non-exclusive,
            non-transferable, revocable license to use our Services for personal or internal business use.
            You must not:</p>
          <ul class="list-disc list-outside pl-6 mt-2 space-y-1 text-left">
            <li>Violate any applicable law or regulation.</li>
            <li>Reverse-engineer, decompile, tamper with, or attempt unauthorized access to our software or systems.</li>
            <li>Use our Services in a way that could harm availability, security, or other users.</li>
          </ul>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">3. Age Requirements</h2>
          <p>Unless otherwise specified, our Services are intended for users aged <strong>13 and older</strong>. Some
            apps may be labeled for children under 13 and will comply with applicable child privacy laws.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">4. Intellectual Property</h2>
          <p>All content and code within our Services is owned by Untracked Apps or its licensors. You may not reuse,
            distribute, or modify our content without permission unless such use is expressly allowed under an open
            source or app-specific license.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">5. Purchases and Billing</h2>
          <p>Some apps may offer optional in-app purchases (including one-time purchases or subscriptions).
            Billing is handled by the platform provider (for example, Apple App Store), and your payment,
            cancellation, and refund rights are governed by that platform’s terms and policies.
            We do not directly process or store your full payment card information.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">6. Privacy</h2>
          <p>We collect as little data as possible. Where data is collected (e.g. for crash reporting or app
            preferences), we describe that in our <a href="/privacy" class="text-blue-600 hover:underline">Privacy
              Policy</a>. If an app includes ads or analytics, related data use is disclosed in that app’s listing
            and policy details.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">7. Clinical and Professional Disclaimer</h2>
          <p>Some apps may provide educational or clinical workflow support information. Unless explicitly stated
            otherwise, our apps do not provide medical, legal, or tax advice and are not a substitute for professional
            judgment, official guidance, or payer-specific rules.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">8. Disclaimers</h2>
          <p>Our Services are provided “as is” and “as available” without warranties of any kind, to the fullest extent
            permitted by law. We do not guarantee uninterrupted availability, error-free operation, or specific outcomes.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">9. Limitation of Liability</h2>
          <p>To the maximum extent allowed by law, Untracked Apps is not liable for any damages or losses arising from
            your use of our Services, including indirect, incidental, consequential, special, or punitive damages.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">10. Changes to These Terms</h2>
          <p>We may update these Terms from time to time. If we make material changes, we’ll update the “Effective Date”
            above. Your continued use of the Services after a change means you accept the updated Terms.</p>
        </section>

        <section>
          <h2 class="text-xl font-semibold mb-2">11. Contact</h2>
          <p>For questions or support, you can reach us at: <a href="mailto:support@untrackedapps.com"
                                                               class="text-blue-600 hover:underline">support@untrackedapps.com</a>
          </p>
        </section>
      </section>
    </section>
  </BasePage>
</template>
